import {createContext, Dispatch, FC, ReactNode, SetStateAction, useContext, useRef, useState} from "react";
import {InkSeriesModel} from "@utils/Models";
import FlintDialogConfirm, {flintDialogConfirmProps} from "@components//controls/dialogs/FlintDialogConfirm";
import {useTranslation} from "react-i18next";

export const FlintDialogContext = createContext<FlintDialogContextProps | null>(null);

export function useFlintDialogContext() {
    const context = useContext(FlintDialogContext);
    if (!context) {
        throw new Error('useFlintDialogContext must be used within an FlintDialogContextProvider');
    }
    return context;
}

export const FlintDialogContextProvider: FC<{ children: ReactNode }> = ({children}) => {
    const {showConfirmationDialog, confirmDialog} = useVivoConfirmation();
    const {t} = useTranslation();

    function showConfirmDialog(props: flintDialogConfirmProps) {
        showConfirmationDialog(props);
    }

    function showAreYouSureDialog(props: {
        onAccept: () => void | Promise<void>, content?: ReactNode, title?: string,
        acceptButtonText?: string, cancelButtonText?: string, isLoading?: boolean
    }) {
        showConfirmationDialog({
            onAccept: props.onAccept,
            content: props.content ?? t("COMMON_AreYouSureQuestion"),
            title: props.title ?? t("COMMON_PleaseConfirm"),
            acceptButtonText: props.acceptButtonText,
            cancelButtonText: props.cancelButtonText,
            isLoading: props.isLoading
        });
    }

    return (
        <FlintDialogContext.Provider value={{showConfirmDialog, showAreYouSureDialog}}>
            {children}
            {confirmDialog}
        </FlintDialogContext.Provider>
    );
};

export interface FlintDialogContextProps {
    showConfirmDialog: (props: flintDialogConfirmProps) => void;
    showAreYouSureDialog: (props: {
        onAccept: () => void | Promise<void>,
        content?: ReactNode,
        title?: string,
        acceptButtonText?: string,
        cancelButtonText?: string,
        isLoading?: boolean,
    }) => void;
}


function useVivoConfirmation() {
    const [dialogVisible, setDialogVisible] = useState<boolean>(false);
    let propsRef = useRef<flintDialogConfirmProps>({} as any);

    function showDialog(p: flintDialogConfirmProps) {
        const {onAccept, onCancel, ...other} = p;
        propsRef.current = {
            ...other,
            onAccept: p.onAccept ? () => handleAccept(p.onAccept!) : undefined,
            onCancel: () => handleCancel(p.onCancel)
        }
        setDialogVisible(true);
    }

    function handleAccept(nativeAccept: () => void | Promise<void>) {
        const result = nativeAccept();

        // Handle both Promise and void cases
        Promise.resolve(result).then(() => {
            setDialogVisible(false);
            propsRef.current = {} as any;
        }).catch(error => {
            // Optional: Handle any errors from the Promise
            console.error('Error occurred during nativeAccept:', error);
        });
    }

    function handleCancel(nativeCancel?: () => void) {
        nativeCancel?.();
        setDialogVisible(false);
        propsRef.current = {} as any;
    }

    const dialog = (dialogVisible && <FlintDialogConfirm {...propsRef.current} />)
    return {
        showConfirmationDialog: showDialog,
        confirmDialog: dialog,
    }
}